import { Injectable } from '@angular/core';
import { ApeCarrierSplitV2 } from '../../dashboard-interfaces/ape-carrier-split-v2.interface';
import { InceptedApeByAdvisorsV2 } from '../../dashboard-interfaces/incepted-ape-by-advisors-v2.interface';
import { InceptedApeByBranchesV2 } from '../../dashboard-interfaces/incepted-ape-by-branches-v2.interface';

/** A row the carrier chips can be rendered for — advisor, manager or branch. */
export type ApeCarrierRowV2 = InceptedApeByAdvisorsV2 | InceptedApeByBranchesV2;

/** One carrier's APE and case count for a single period, as the API would return it. */
export interface ApeCompanyBreakdownV2 {
  company: string;
  ape: number;
  cases: number;
}

/**
 * Carrier colours, monograms and the per-row MTD/YTD split behind the in-cell
 * carrier chips (design 1c).
 *
 * Shared by the Incepted APE tables on the dashboard and the by-manager /
 * by-branch advisor drill-down dialogs so every table splits, colours and
 * labels its carriers identically.
 */
@Injectable({
  providedIn: 'root'
})
export class ApeCarrierSplitServiceV2 {

  /** Rows the split never reached render no chips rather than throwing. */
  private static readonly EMPTY_SPLIT: ApeCarrierSplitV2[] = [];

  // Colour per company; unknown companies cycle the fallback palette.
  private readonly companyColors: Record<string, string> = {
    'singlife': '#3d8bf0',
    'china life': '#e5484d',
    'china taiping': '#db2777',
    'etiqa': '#f59e0b',
    'fwd': '#f97316',
    'income': '#16a34a',
    'manulife': '#12a594',
    'rhi': '#0891b2',
    'tokio marine': '#2563eb',
    'transamerica': '#6366f1',
    'ifast': '#8b5cf6',
    'grow': '#a855f7',
    'tiger': '#64748b',
    'fwdember': '#18d695',
    'hsbc': '#774ee9',
    'other companies': '#12a594'
  };
  private readonly fallbackPalette: string[] = ['#0ea5e9', '#6366f1', '#ec4899', '#14b8a6', '#f97316', '#8b5cf6', '#e5484d', '#f59e0b'];

  // Company logos by name. Drop the real files at these paths; any that are
  // missing fall back to the coloured monogram (see the drawer's error handler).
  private readonly companyLogos: Record<string, string> = {
    'singlife': 'assets/images/carriers/singlife_logo.png',
    'china life': 'assets/images/carriers/china-life.png',
    'china taiping': 'assets/images/carriers/china-taiping.png',
    'etiqa': 'assets/images/carriers/etiqa.png',
    'fwd': 'assets/images/carriers/fwd.png',
    'income': 'assets/images/carriers/income.png',
    'manulife': 'assets/images/carriers/manulife.png',
    'rhi': 'assets/images/carriers/rhi.png',
    'tokio marine': 'assets/images/carriers/tokio-marine.png',
    'transamerica': 'assets/images/carriers/transamerica.png',
    'ifast': 'assets/images/carriers/ifast.png',
    'grow': 'assets/images/carriers/grow.png',
    'tiger': 'assets/images/carriers/tiger.png',
    'fwdember': 'assets/images/carriers/fwd.png',
    'hsbc': 'assets/images/carriers/hsbc.png',
  };

  // ⚠️ TEMPORARY: preview the per-company breakdown with sample carriers until
  // the backend returns a real per-company array. Set to false (or let the API
  // supply `mtd/ytdCompanies`) to drop back to the single "Other Companies" row.
  readonly PREVIEW_COMPANY_BREAKDOWN = true;

  colorForCompany(name: string, index: number): string {
    return this.companyColors[(name || '').trim().toLowerCase()]
      ?? this.fallbackPalette[index % this.fallbackPalette.length];
  }

  logoForCompany(name: string): string | undefined {
    return this.companyLogos[(name || '').trim().toLowerCase()];
  }

  initialOf(name: string): string {
    return (name || '').trim().charAt(0).toUpperCase() || '?';
  }

  /**
   * ⚠️ TEMPORARY preview data — remove once the backend returns a real
   * per-company breakdown. Splits the aggregated Non-Singlife total across
   * sample carriers so the card's total / bar / percentages stay consistent.
   * The last carrier absorbs the rounding remainder so the parts sum exactly.
   */
  buildMockCompanies(nonSinglife: number, nonSinglifeCase: number): ApeCompanyBreakdownV2[] {
    const weights = [
      { company: 'China Life', weight: 0.10 },
      { company: 'China Taiping', weight: 0.07 },
      { company: 'Etiqa', weight: 0.09 },
      { company: 'FWD', weight: 0.11 },
      { company: 'Income', weight: 0.13 },
      { company: 'Manulife', weight: 0.16 },
      { company: 'RHI', weight: 0.05 },
      { company: 'Tokio Marine', weight: 0.08 },
      { company: 'Transamerica', weight: 0.04 },
      { company: 'iFAST', weight: 0.06 },
      { company: 'GROW', weight: 0.055 },
      { company: 'TIGER', weight: 0.035 }
    ];

    let apeRemaining = nonSinglife;
    let caseRemaining = nonSinglifeCase;

    return weights.map((entry, index) => {
      const isLast = index === weights.length - 1;
      const ape = isLast ? apeRemaining : Math.round(nonSinglife * entry.weight);
      const cases = isLast ? caseRemaining : Math.round(nonSinglifeCase * entry.weight);
      apeRemaining -= ape;
      caseRemaining -= cases;
      return { company: entry.company, ape, cases };
    });
  }

  /**
   * Fills in each row's MTD/YTD totals and its per-carrier split, which is what
   * the carrier-chip cells render.
   *
   * ⚠️ API-shape assumption — cross-check against the Go backend. The list
   * endpoints return only the aggregated `mtd/ytdSinglife` and
   * `mtd/ytdNonSinglife` pairs per row, with no per-carrier array. Until one
   * exists, the non-Singlife total is split across the same sample carriers the
   * summary cards preview (`buildMockCompanies`). When the backend starts
   * returning a per-row carrier array, replace the `buildMockCompanies` calls
   * here with it — nothing else in the cells has to change.
   */
  applyCarrierSplit(rows: ApeCarrierRowV2[]): void {
    rows.forEach((row) => {


      row.monthTotal =
        Number.parseInt(row.monthSinglife.toString()) +
        Number.parseInt(row.monthChinalife.toString()) +
        Number.parseInt(row.monthChinataiping.toString()) +
        Number.parseInt(row.monthEtiqa.toString()) +
        Number.parseInt(row.monthFwd.toString()) +
        Number.parseInt(row.monthGrow.toString()) +
        Number.parseInt(row.monthIfast.toString()) +
        Number.parseInt(row.monthManulife.toString()) +
        Number.parseInt(row.monthIncome.toString()) +
        Number.parseInt(row.monthRhi.toString()) +
        Number.parseInt(row.monthTiger.toString()) +
        Number.parseInt(row.monthTokiomarine.toString()) +
        Number.parseInt(row.monthTransamerica.toString()) +
        Number.parseInt(row.monthFwdEmber.toString()) +
        Number.parseInt(row.monthHsbc.toString());

      row.monthTotalCase =
        Number.parseInt(row.monthSinglifeCase.toString()) +
        Number.parseInt(row.monthChinalifeCase.toString()) +
        Number.parseInt(row.monthChinataipingCase.toString()) +
        Number.parseInt(row.monthEtiqaCase.toString()) +
        Number.parseInt(row.monthFwdCase.toString()) +
        Number.parseInt(row.monthGrowCase.toString()) +
        Number.parseInt(row.monthIfastCase.toString()) +
        Number.parseInt(row.monthManulifeCase.toString()) +
        Number.parseInt(row.monthIncomeCase.toString()) +
        Number.parseInt(row.monthRhiCase.toString()) +
        Number.parseInt(row.monthTigerCase.toString()) +
        Number.parseInt(row.monthTokiomarineCase.toString()) +
        Number.parseInt(row.monthTransamericaCase.toString()) +
        Number.parseInt(row.monthFwdEmberCase.toString()) +
        Number.parseInt(row.monthHsbcCase.toString());

      row.yearTotalCase =
        Number.parseInt(row.yearSinglifeCase.toString()) +
        Number.parseInt(row.yearChinalifeCase.toString()) +
        Number.parseInt(row.yearChinataipingCase.toString()) +
        Number.parseInt(row.yearEtiqaCase.toString()) +
        Number.parseInt(row.yearFwdCase.toString()) +
        Number.parseInt(row.yearGrowCase.toString()) +
        Number.parseInt(row.yearIfastCase.toString()) +
        Number.parseInt(row.yearManulifeCase.toString()) +
        Number.parseInt(row.yearIncomeCase.toString()) +
        Number.parseInt(row.yearRhiCase.toString()) +
        Number.parseInt(row.yearTigerCase.toString()) +
        Number.parseInt(row.yearTokiomarineCase.toString()) +
        Number.parseInt(row.yearTransamericaCase.toString()) +
        Number.parseInt(row.yearFwdEmberCase.toString()) +
        Number.parseInt(row.yearHsbcCase.toString());

      row.yearTotal =
        Number.parseInt(row.yearSinglife.toString()) +
        Number.parseInt(row.yearChinalife.toString()) +
        Number.parseInt(row.yearChinataiping.toString()) +
        Number.parseInt(row.yearEtiqa.toString()) +
        Number.parseInt(row.yearFwd.toString()) +
        Number.parseInt(row.yearGrow.toString()) +
        Number.parseInt(row.yearIfast.toString()) +
        Number.parseInt(row.yearManulife.toString()) +
        Number.parseInt(row.yearIncome.toString()) +
        Number.parseInt(row.yearRhi.toString()) +
        Number.parseInt(row.yearTiger.toString()) +
        Number.parseInt(row.yearTokiomarine.toString()) +
        Number.parseInt(row.yearTransamerica.toString()) +
        Number.parseInt(row.yearFwdEmber.toString()) +
        Number.parseInt(row.yearHsbc.toString());

      row.carriers = this.buildRowCarriers(row);

      // Shares drive the mix bar, so they are measured against the row's own
      // totals — a row with no APE gets no bar rather than a divide-by-zero.
      row.carriers.forEach((carrier) => {
        carrier.mtdShare = this.shareOf(carrier.mtdApe, row.monthTotal ?? 0);
        carrier.ytdShare = this.shareOf(carrier.ytdApe, row.yearTotal);
      });
    });
  }

  /** Carriers to render for a row, tolerant of rows the split never reached. */
  carriersOf(row: ApeCarrierRowV2): ApeCarrierSplitV2[] {
    return (row.carriers ?? ApeCarrierSplitServiceV2.EMPTY_SPLIT)
      .filter(carrier =>
        (carrier.mtdApe > 0 && carrier.mtdCase > 0) ||
        (carrier.ytdApe > 0 && carrier.ytdCase > 0)
      );
  }

  /** Singlife first, then one entry per non-Singlife carrier, MTD and YTD aligned. */
  private buildRowCarriers(row: ApeCarrierRowV2): ApeCarrierSplitV2[] {
    const carriers: ApeCarrierSplitV2[] = [
      {
        name: 'Singlife',
        initial: 'S',
        color: this.colorForCompany('Singlife', 0),
        mtdApe: Number.parseInt(row.monthSinglife.toString()),
        mtdCase: Number.parseInt(row.monthSinglifeCase.toString()),
        ytdApe: Number.parseInt(row.yearSinglife.toString()),
        ytdCase: Number.parseInt(row.yearSinglifeCase.toString()),
        mtdShare: '0%',
        ytdShare: '0%'
      },
      {
        name: 'China Life',
        initial: 'C',
        color: this.colorForCompany('China Life', 0),
        mtdApe: Number.parseInt(row.monthChinalife.toString()),
        mtdCase: Number.parseInt(row.monthChinalifeCase.toString()),
        ytdApe: Number.parseInt(row.yearChinalife.toString()),
        ytdCase: Number.parseInt(row.yearChinalifeCase.toString()),
        mtdShare: '0%',
        ytdShare: '0%'
      },
      {
        name: 'China Taiping',
        initial: 'C',
        color: this.colorForCompany('China Taiping', 0),
        mtdApe: Number.parseInt(row.monthChinataiping.toString()),
        mtdCase: Number.parseInt(row.monthChinataipingCase.toString()),
        ytdApe: Number.parseInt(row.yearChinataiping.toString()),
        ytdCase: Number.parseInt(row.yearChinataipingCase.toString()),
        mtdShare: '0%',
        ytdShare: '0%'
      },
      {
        name: 'Etiqa',
        initial: 'E',
        color: this.colorForCompany('Etiqa', 0),
        mtdApe: Number.parseInt(row.monthEtiqa.toString()),
        mtdCase: Number.parseInt(row.monthEtiqaCase.toString()),
        ytdApe: Number.parseInt(row.yearEtiqa.toString()),
        ytdCase: Number.parseInt(row.yearEtiqaCase.toString()),
        mtdShare: '0%',
        ytdShare: '0%'
      },
      {
        name: 'FWD',
        initial: 'F',
        color: this.colorForCompany('FWD', 0),
        mtdApe: Number.parseInt(row.monthFwd.toString()),
        mtdCase: Number.parseInt(row.monthFwdCase.toString()),
        ytdApe: Number.parseInt(row.yearFwd.toString()),
        ytdCase: Number.parseInt(row.yearFwdCase.toString()),
        mtdShare: '0%',
        ytdShare: '0%'
      },
      {
        name: 'Income',
        initial: 'I',
        color: this.colorForCompany('Income', 0),
        mtdApe: Number.parseInt(row.monthIncome.toString()),
        mtdCase: Number.parseInt(row.monthIncomeCase.toString()),
        ytdApe: Number.parseInt(row.yearIncome.toString()),
        ytdCase: Number.parseInt(row.yearIncomeCase.toString()),
        mtdShare: '0%',
        ytdShare: '0%'
      },
      {
        name: 'Manulife',
        initial: 'M',
        color: this.colorForCompany('Manulife', 0),
        mtdApe: Number.parseInt(row.monthManulife.toString()),
        mtdCase: Number.parseInt(row.monthManulifeCase.toString()),
        ytdApe: Number.parseInt(row.yearManulife.toString()),
        ytdCase: Number.parseInt(row.yearManulifeCase.toString()),
        mtdShare: '0%',
        ytdShare: '0%'
      },
      {
        name: 'RHI',
        initial: 'R',
        color: this.colorForCompany('RHI', 0),
        mtdApe: Number.parseInt(row.monthRhi.toString()),
        mtdCase: Number.parseInt(row.monthRhiCase.toString()),
        ytdApe: Number.parseInt(row.yearRhi.toString()),
        ytdCase: Number.parseInt(row.yearRhiCase.toString()),
        mtdShare: '0%',
        ytdShare: '0%'
      },
      {
        name: 'Tokio Marine',
        initial: 'T',
        color: this.colorForCompany('Tokio Marine', 0),
        mtdApe: Number.parseInt(row.monthTokiomarine.toString()),
        mtdCase: Number.parseInt(row.monthTokiomarineCase.toString()),
        ytdApe: Number.parseInt(row.yearTokiomarine.toString()),
        ytdCase: Number.parseInt(row.yearTokiomarineCase.toString()),
        mtdShare: '0%',
        ytdShare: '0%'
      },
      {
        name: 'Transamerica',
        initial: 'T',
        color: this.colorForCompany('Transamerica', 0),
        mtdApe: Number.parseInt(row.monthTransamerica.toString()),
        mtdCase: Number.parseInt(row.monthTransamericaCase.toString()),
        ytdApe: Number.parseInt(row.yearTransamerica.toString()),
        ytdCase: Number.parseInt(row.yearTransamericaCase.toString()),
        mtdShare: '0%',
        ytdShare: '0%'
      },
      {
        name: 'iFAST',
        initial: 'I',
        color: this.colorForCompany('iFAST', 0),
        mtdApe: Number.parseInt(row.monthIfast.toString()),
        mtdCase: Number.parseInt(row.monthIfastCase.toString()),
        ytdApe: Number.parseInt(row.yearIfast.toString()),
        ytdCase: Number.parseInt(row.yearIfastCase.toString()),
        mtdShare: '0%',
        ytdShare: '0%'
      },
      {
        name: 'GROW',
        initial: 'G',
        color: this.colorForCompany('GROW', 0),
        mtdApe: Number.parseInt(row.monthGrow.toString()),
        mtdCase: Number.parseInt(row.monthGrowCase.toString()),
        ytdApe: Number.parseInt(row.yearGrow.toString()),
        ytdCase: Number.parseInt(row.yearGrowCase.toString()),
        mtdShare: '0%',
        ytdShare: '0%'
      },
      {
        name: 'TIGER',
        initial: 'T',
        color: this.colorForCompany('TIGER', 0),
        mtdApe: Number.parseInt(row.monthTiger.toString()),
        mtdCase: Number.parseInt(row.monthTigerCase.toString()),
        ytdApe: Number.parseInt(row.yearTiger.toString()),
        ytdCase: Number.parseInt(row.yearTigerCase.toString()),
        mtdShare: '0%',
        ytdShare: '0%'
      },
      {
        name: 'FWD Ember',
        initial: 'F',
        color: this.colorForCompany('FWD Ember', 0),
        mtdApe: Number.parseInt(row.monthFwdEmber.toString()),
        mtdCase: Number.parseInt(row.monthFwdEmberCase.toString()),
        ytdApe: Number.parseInt(row.yearFwdEmber.toString()),
        ytdCase: Number.parseInt(row.yearFwdEmberCase.toString()),
        mtdShare: '0%',
        ytdShare: '0%'
      },
      {
        name: 'HSBC',
        initial: 'H',
        color: this.colorForCompany('HSBC', 0),
        mtdApe: Number.parseInt(row.monthHsbc.toString()),
        mtdCase: Number.parseInt(row.monthHsbcCase.toString()),
        ytdApe: Number.parseInt(row.yearHsbc.toString()),
        ytdCase: Number.parseInt(row.yearHsbcCase.toString()),
        mtdShare: '0%',
        ytdShare: '0%'
      }
    ];

    return carriers;
  }

  private shareOf(value: number, total: number): string {
    return total > 0 ? ((value / total) * 100).toFixed(2) + '%' : '0%';
  }
}
